/**
 * BridgeStatusCard — generic "this bridge is/isn't running" panel used by
 * both the Telegram-user (MTProto) wizard and the WhatsApp (Baileys)
 * wizard.
 *
 * Bridges are docker containers we ship but operators deploy separately
 * (each requires a phone number + secrets). The pattern:
 *
 *   1. Operator installs a connector row via the same `/connectors/install`
 *      endpoint, supplying a `bridge_secret`.
 *   2. We render a copy-paste docker-compose snippet pre-filled with the
 *      installation_id + bridge_secret.
 *   3. Operator runs the container; it reaches back via /external-intake.
 *   4. As soon as the first message arrives, /intake/stats[channel] ticks
 *      up — we poll and show the live counter so the wizard "lights up"
 *      the moment the bridge is alive.
 */

import { useEffect, useState } from "react";
import { Activity, Copy, ExternalLink, ShieldCheck } from "lucide-react";
import { Badge, Button, Card, CardHeader, Input } from "@vseh/ui";

import { connectorsApi, intakeApi, type InstallationDto } from "../../shared/api";


export interface BridgeWizardProps {
  /** API connector_name (`telegram-user` | `whatsapp`). */
  connectorName: string;
  /** Channel name as the intake-classifier sees it (matches `by_channel` key). */
  channelKey:    string;
  /** Human-readable bridge title — "Telegram-аккаунт" / "WhatsApp". */
  title:         string;
  /** Short one-liner shown under the title. */
  subtitle:      string;
  /** Link to BotFather-equivalent docs (e.g. https://my.telegram.org). */
  helpUrl?:      string;
  helpLabel?:    string;
  /** Extra config fields the operator must supply (e.g. phone, api_id). */
  extraFields:   { name: string; label: string; placeholder?: string; type?: string }[];
  /** Compose snippet template — substitutions: {INSTALLATION_ID}, {BRIDGE_SECRET}. */
  composeSnippet:(installation: string, secret: string) => string;

  existing: InstallationDto | undefined;
  onChange: () => void;
}


export function BridgeStatusCard({
  connectorName, channelKey, title, subtitle,
  helpUrl, helpLabel, extraFields, composeSnippet,
  existing, onChange,
}: BridgeWizardProps): JSX.Element {
  const [secret] = useState(() => randomSecret());
  const [installing, setInstalling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [newlyInstalled, setNewlyInstalled] = useState<InstallationDto | null>(null);

  const installation = newlyInstalled ?? existing ?? null;
  const [recentCount, setRecentCount] = useState<number | null>(null);

  useEffect(() => {
    if (!installation) return;
    let alive = true;
    const tick = async () => {
      try {
        const s = await intakeApi.stats(24);
        if (alive) setRecentCount(s.by_channel?.[channelKey] ?? 0);
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 4_000);
    return () => { alive = false; clearInterval(id); };
  }, [installation?.id, channelKey]);

  const onInstall = async () => {
    setInstalling(true); setError(null);
    try {
      const config: Record<string, string> = { bridge_secret: secret };
      // Allow placeholder values — operator usually replaces them after
      // they grab API credentials from my.telegram.org / phone setup.
      for (const f of extraFields) {
        const v = (fields[f.name] || "").trim();
        if (v) config[f.name] = v;
      }
      const r = await connectorsApi.install({
        connector_name: connectorName, enabled: true, config,
      });
      setNewlyInstalled(r); onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setInstalling(false);
    }
  };

  // ── Connected — show counter & compose snippet for restart guidance.
  if (installation) {
    const snippet = composeSnippet(installation.id, "—   (хранится зашифрованно)");
    return (
      <Card>
        <CardHeader
          title={title}
          description={`${subtitle} · версия ${installation.connector_version}`}
          actions={
            <Badge tone={installation.enabled ? "ai" : "heritage"}>
              {installation.enabled ? "установлен" : "выключен"}
            </Badge>
          }
        />
        <div className="space-y-3 text-sm">
          {recentCount !== null && (
            <div className="flex items-center gap-2 text-base-700">
              <Activity className="size-4 text-ai-600" />
              <span>
                За 24ч получено сообщений из <b>{channelKey}</b>:{" "}
                <span className="font-mono tabular-nums">{recentCount}</span>
              </span>
            </div>
          )}
          <details className="rounded-md border border-base-200 bg-base-50 p-3">
            <summary className="cursor-pointer text-base-700">
              Snippet для <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">docker-compose.yml</code>
            </summary>
            <pre className="mt-2 overflow-x-auto font-mono text-2xs text-base-900">
              {snippet}
            </pre>
          </details>
        </div>
      </Card>
    );
  }

  // ── Fresh install — collect fields, install, show step 3 with compose.
  return (
    <Card>
      <CardHeader
        title={`Подключить ${title}`}
        description={subtitle}
      />
      <ol className="space-y-4 text-sm">
        {helpUrl && (
          <li className="flex gap-3">
            <span className="grid size-7 shrink-0 place-items-center rounded-full bg-ai-100 font-display text-sm font-semibold text-ai-800">1</span>
            <div className="min-w-0 flex-1">
              <div className="font-medium text-base-900">{helpLabel ?? "Подготовка"}</div>
              <p className="mt-1 text-base-600">
                Откройте{" "}
                <a href={helpUrl} target="_blank" rel="noopener"
                   className="inline-flex items-center gap-1 text-ai-700 underline">
                  {helpUrl.replace(/^https?:\/\//, "")}
                  <ExternalLink className="size-3" />
                </a>
                {" "}и следуйте инструкциям, чтобы получить нужные ключи.
              </p>
            </div>
          </li>
        )}
        <li className="flex gap-3">
          <span className="grid size-7 shrink-0 place-items-center rounded-full bg-ai-100 font-display text-sm font-semibold text-ai-800">
            {helpUrl ? "2" : "1"}
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-medium text-base-900">Сохраните учётные данные</div>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {extraFields.map((f) => (
                <Input
                  key={f.name}
                  type={f.type ?? "text"}
                  label={f.label}
                  placeholder={f.placeholder}
                  value={fields[f.name] ?? ""}
                  onChange={(e) => setFields((p) => ({ ...p, [f.name]: e.target.value }))}
                  disabled={installing}
                />
              ))}
            </div>
            <div className="mt-2">
              <Button variant="ai" onClick={onInstall} loading={installing}>
                Создать инсталляцию
              </Button>
            </div>
            {error && (
              <div className="mt-1.5 text-xs text-danger-700">{error}</div>
            )}
            <p className="mt-1 text-2xs text-base-500">
              <ShieldCheck className="inline size-3 align-text-bottom" /> Все поля
              шифруются envelope-AES. Bridge-секрет генерируется в браузере
              и передаётся контейнеру только через переменную окружения.
            </p>
          </div>
        </li>
        {newlyInstalled && (
          <li className="flex gap-3">
            <span className="grid size-7 shrink-0 place-items-center rounded-full bg-ai-100 font-display text-sm font-semibold text-ai-800">
              {helpUrl ? "3" : "2"}
            </span>
            <div className="min-w-0 flex-1">
              <div className="font-medium text-base-900">
                Поднимите бридж-контейнер
              </div>
              <p className="mt-1 text-base-600">
                Добавьте сервис в <code className="rounded bg-base-50 px-1 font-mono text-xs">docker-compose.yml</code>:
              </p>
              <CopyableBlock value={composeSnippet(newlyInstalled.id, secret)} />
              <p className="mt-2 text-2xs text-base-500">
                После <code className="rounded bg-base-50 px-1 font-mono text-xs">docker compose up -d {connectorName}</code>{" "}
                контейнер залогинится, начнёт читать сообщения и каждые 2-4 секунды
                будет POST'ить их сюда → <a href="/intake" className="text-ai-700 underline">/intake</a>.
              </p>
            </div>
          </li>
        )}
      </ol>
    </Card>
  );
}


function CopyableBlock({ value }: { value: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };
  return (
    <div className="relative mt-2">
      <pre className="overflow-x-auto rounded-md border border-base-200 bg-base-50 p-3 pr-12 font-mono text-2xs text-base-900">
        {value}
      </pre>
      <button
        onClick={onCopy}
        className="absolute right-2 top-2 rounded-md border border-base-200 bg-surface px-2 py-1 text-2xs text-base-600 hover:bg-base-50"
        title="Скопировать"
      >
        {copied ? "✓" : <Copy className="size-3" />}
      </button>
    </div>
  );
}


function randomSecret(): string {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let out = "";
  const buf = new Uint32Array(32);
  crypto.getRandomValues(buf);
  for (let i = 0; i < 32; i++) {
    const idx = (buf[i] ?? 0) % alphabet.length;
    out += alphabet.charAt(idx);
  }
  return out;
}
